import React from "react";
import { Link } from "react-router-dom";
import { Sparkles, Check, ArrowRight, Zap, Building2, Shield } from "lucide-react";
import RevealOnScroll from "@/components/ui/RevelOnScroll";
import SleekButton from "@/components/ui/NavButtons";
import { SEO } from "@/components/common/SEO";

const APP_URL = "https://app.vawena.ai";

const WEALTHSCOPE_FEATURES = [
  "Investment readiness score",
  "Net worth dashboard & tracking",
  "Buying power & investment capacity",
  "Debt-to-income (DTI) analysis",
  "Monthly cash flow breakdown",
  "Bank account sync via Plaid",
  "Investment milestone tracker",
  "AI-powered financial insights",
  "Automated alerts & notifications",
  "Financial reports (PDF)",
];

const PORTFOLIOIQ_FEATURES = [
  "Unlimited property tracking",
  "Cap rate & cash-on-cash returns",
  "DSCR analysis & lender PDF reports",
  "Schedule E tax reports for your CPA",
  "Property performance dashboards",
  "Portfolio-level analytics",
  "Market value benchmarking",
  "Rent & income tracking",
  "Lease & tenant records",
  "Performance alerts",
];

const BUNDLE_EXTRAS = [
  "Priority support",
  "Early access to new features",
  "Unlimited bank connections",
];

const WHY_BUNDLE = [
  {
    title: "Know when to buy. Know how you're performing.",
    desc: "WealthScope tells you if you're financially ready for your next deal. PortfolioIQ shows you how your current properties are actually performing. Together, they cover every stage of the real estate investor lifecycle.",
  },
  {
    title: "Save $59.88/year vs. buying separately",
    desc: "WealthScope + PortfolioIQ separately costs $39.98/month. The Bundle is $34.99/month — you save nearly $60 a year on monthly billing alone.",
  },
  {
    title: "Your CPA and lender, handled",
    desc: "Schedule E reports for your accountant and DSCR lender reports for your mortgage broker — both included. No more building decks or spreadsheets from scratch.",
  },
  {
    title: "Every acquisition updates your full picture",
    desc: "See exactly how each new property changes your DTI, buying power, and deal readiness. Your financial health and portfolio analytics stay in sync automatically.",
  },
];

const schema = {
  "@context": "https://schema.org",
  "@type": "SoftwareApplication",
  "name": "VaWeNa Bundle — WealthScope + PortfolioIQ",
  "applicationCategory": "FinanceApplication",
  "description": "The complete real estate investor platform. WealthScope tracks net worth and investment readiness. PortfolioIQ analyzes portfolio performance, DSCR, and generates Schedule E and lender reports.",
  "offers": { "@type": "Offer", "price": "34.99", "priceCurrency": "USD" },
  "url": "https://vawena.ai/bundle",
};

const BundlePage: React.FC = () => {
  return (
    <>
      <SEO
        title="VaWeNa Bundle — WealthScope + PortfolioIQ for Real Estate Investors"
        description="Get WealthScope and PortfolioIQ together. Track net worth, analyze portfolio performance, generate DSCR lender reports and Schedule E tax reports — all for $34.99/month. 14-day free trial."
        canonical="/bundle"
        schema={schema}
      />

      <div className="min-h-screen bg-vw-bg">
        {/* ── Hero ─────────────────────────────────────────────── */}
        <section className="relative pt-20 pb-24 px-4 sm:px-6 overflow-hidden">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[700px] h-[450px] bg-blue-600/10 rounded-full blur-3xl pointer-events-none" />

          <div className="max-w-[1000px] mx-auto text-center relative z-10">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-blue-500/10 border border-blue-500/20 text-blue-400 text-sm font-semibold mb-6">
              <Sparkles className="w-4 h-4" />
              VaWeNa Bundle · Best Value
            </div>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white leading-tight mb-5">
              The Complete Real Estate{" "}
              <span className="bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">Investor Platform</span>
            </h1>

            <p className="text-lg text-slate-400 max-w-2xl mx-auto mb-4 leading-relaxed">
              WealthScope tells you when you're ready to buy. PortfolioIQ shows you how your existing properties are performing. Together in one subscription — with lender reports and Schedule E summaries included.
            </p>

            {/* Audience callout */}
            <p className="text-sm text-blue-400 font-semibold mb-6">
              Built for serious investors who want financial clarity at every stage.
            </p>

            {/* Price comparison */}
            <div className="inline-flex items-center gap-3 bg-white/5 border border-white/10 rounded-2xl px-6 py-4 mb-10">
              <div className="text-center">
                <p className="text-xs text-slate-500 mb-0.5">Separately</p>
                <p className="text-lg font-bold text-slate-400 line-through">$39.98/mo</p>
              </div>
              <div className="w-px h-10 bg-white/10" />
              <div className="text-center">
                <p className="text-xs text-blue-400 font-semibold mb-0.5">Bundle price</p>
                <p className="text-2xl font-extrabold text-white">$34.99/mo</p>
              </div>
              <div className="w-px h-10 bg-white/10" />
              <div className="text-center">
                <p className="text-xs text-slate-500 mb-0.5">You save</p>
                <p className="text-lg font-bold text-blue-400">$59.88/yr</p>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <SleekButton variant="primary" onClick={() => window.open(`${APP_URL}/signup`, "_self")}>
                Start Free 14-Day Trial
              </SleekButton>
              <Link to="/pricing">
                <SleekButton variant="light">View All Plans →</SleekButton>
              </Link>
            </div>
            <p className="mt-4 text-sm text-slate-500">Credit card required · We'll remind you 3 days before you're charged · Cancel anytime</p>
          </div>
        </section>

        {/* ── Stats ─────────────────────────────────────────────── */}
        <RevealOnScroll direction="up">
          <section className="py-12 px-4 border-y border-white/5">
            <div className="max-w-[1000px] mx-auto grid grid-cols-2 sm:grid-cols-4 gap-8 text-center">
              {[
                { value: "$34.99", label: "per month, everything included" },
                { value: "$349.90", label: "per year — save 17%" },
                { value: "20+", label: "features across both modules" },
                { value: "14 days", label: "free trial — cancel anytime" },
              ].map(({ value, label }) => (
                <div key={label}>
                  <p className="text-2xl sm:text-3xl font-extrabold text-white mb-1">{value}</p>
                  <p className="text-sm text-slate-500">{label}</p>
                </div>
              ))}
            </div>
          </section>
        </RevealOnScroll>

        {/* ── Why bundle ───────────────────────────────────────── */}
        <RevealOnScroll direction="up">
          <section className="py-20 px-4 sm:px-6">
            <div className="max-w-[1000px] mx-auto">
              <div className="text-center mb-12">
                <p className="text-xs font-bold uppercase tracking-widest text-blue-500 mb-3">Why bundle</p>
                <h2 className="text-3xl font-extrabold text-white mb-4">
                  WealthScope and PortfolioIQ are better together
                </h2>
                <p className="text-slate-400 max-w-xl mx-auto">
                  Most serious investors need both — the clarity to know when to buy next, and the analytics to know how their current properties are actually doing.
                </p>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                {WHY_BUNDLE.map(({ title, desc }) => (
                  <div key={title} className="bg-white/[0.02] border border-white/10 rounded-2xl p-6 hover:border-blue-500/30 hover:bg-blue-500/5 transition-all duration-300">
                    <div className="w-2 h-2 rounded-full bg-blue-400 mb-4" />
                    <h3 className="text-white font-semibold mb-2">{title}</h3>
                    <p className="text-sm text-slate-500 leading-relaxed">{desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>
        </RevealOnScroll>

        {/* ── What's included ──────────────────────────────────── */}
        <RevealOnScroll direction="up">
          <section className="py-16 px-4 sm:px-6 bg-white/[0.01]">
            <div className="max-w-[1000px] mx-auto">
              <div className="text-center mb-12">
                <p className="text-xs font-bold uppercase tracking-widest text-blue-500 mb-3">What's included</p>
                <h2 className="text-3xl font-extrabold text-white">Everything in both modules</h2>
              </div>

              <div className="grid sm:grid-cols-2 gap-6">
                {/* WealthScope */}
                <div className="bg-white/[0.02] border border-white/10 rounded-2xl p-6">
                  <div className="flex items-center gap-3 mb-5">
                    <div className="w-9 h-9 rounded-xl bg-blue-500/10 flex items-center justify-center">
                      <Zap className="w-4 h-4 text-blue-400" />
                    </div>
                    <div>
                      <p className="text-white font-bold">WealthScope</p>
                      <p className="text-xs text-slate-500">Financial intelligence & readiness</p>
                    </div>
                  </div>
                  <ul className="space-y-2">
                    {WEALTHSCOPE_FEATURES.map((f) => (
                      <li key={f} className="flex items-center gap-2.5 text-sm text-slate-300">
                        <Check className="w-3.5 h-3.5 text-blue-400 shrink-0" />
                        {f}
                      </li>
                    ))}
                  </ul>
                </div>

                {/* PortfolioIQ */}
                <div className="bg-white/[0.02] border border-white/10 rounded-2xl p-6">
                  <div className="flex items-center gap-3 mb-5">
                    <div className="w-9 h-9 rounded-xl bg-blue-500/10 flex items-center justify-center">
                      <Building2 className="w-4 h-4 text-blue-400" />
                    </div>
                    <div>
                      <p className="text-white font-bold">PortfolioIQ</p>
                      <p className="text-xs text-slate-500">Portfolio analytics & reports</p>
                    </div>
                  </div>
                  <ul className="space-y-2">
                    {PORTFOLIOIQ_FEATURES.map((f) => (
                      <li key={f} className="flex items-center gap-2.5 text-sm text-slate-300">
                        <Check className="w-3.5 h-3.5 text-blue-400 shrink-0" />
                        {f}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* Bundle extras */}
              <div className="mt-4 bg-blue-500/5 border border-blue-500/20 rounded-2xl p-5">
                <p className="text-xs font-bold uppercase tracking-widest text-blue-400 mb-3">Bundle exclusives</p>
                <div className="flex flex-wrap gap-3">
                  {BUNDLE_EXTRAS.map((f) => (
                    <div key={f} className="flex items-center gap-2 text-sm text-slate-300">
                      <Sparkles className="w-3.5 h-3.5 text-blue-400 shrink-0" />
                      {f}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>
        </RevealOnScroll>

        {/* ── Pricing CTA ──────────────────────────────────────── */}
        <RevealOnScroll direction="up">
          <section className="py-20 px-4 sm:px-6">
            <div className="max-w-[560px] mx-auto bg-white/[0.03] border border-blue-500/20 rounded-3xl p-8 sm:p-10 text-center">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-500/10 border border-blue-500/20 text-blue-400 text-sm font-semibold mb-2">
                <Sparkles className="w-3.5 h-3.5" />
                Best Value
              </div>
              <div className="mt-4 mb-1">
                <p className="text-xs text-slate-500 mb-1">WealthScope + PortfolioIQ separately</p>
                <p className="text-slate-500 line-through text-lg font-semibold">$39.98/month</p>
              </div>
              <h2 className="text-3xl font-extrabold text-white mb-2">VaWeNa Bundle</h2>
              <div className="flex items-end justify-center gap-1 mb-1">
                <span className="text-5xl font-extrabold text-white">$34.99</span>
                <span className="text-slate-500 mb-1.5">/month</span>
              </div>
              <p className="text-sm text-blue-400 font-semibold mb-2">or $349.90/year — save 17% billed annually</p>
              <p className="text-xs text-slate-500 mb-7">
                <Check className="w-3 h-3 inline mr-1 text-blue-400" />
                14-day free trial · We'll remind you before you're charged
              </p>
              <button
                type="button"
                onClick={() => window.open(`${APP_URL}/signup`, "_self")}
                className="w-full h-12 rounded-full bg-blue-600 hover:bg-blue-500 text-white font-bold text-sm transition-colors mb-3"
              >
                Start Free 14-Day Trial
              </button>
              <p className="text-xs text-slate-500">Cancel anytime. We'll email you 3 days before your trial ends.</p>
            </div>
          </section>
        </RevealOnScroll>

        {/* ── Module comparison ────────────────────────────────── */}
        <RevealOnScroll direction="up">
          <section className="py-12 px-4 sm:px-6">
            <div className="max-w-[700px] mx-auto text-center">
              <p className="text-slate-500 text-sm mb-6">Not sure which plan is right for you?</p>
              <div className="grid sm:grid-cols-2 gap-4">
                <Link to="/wealthscope" className="block bg-white/[0.02] border border-white/10 rounded-2xl p-5 hover:border-blue-500/30 transition-colors text-left">
                  <div className="flex items-center gap-2 mb-2">
                    <Zap className="w-4 h-4 text-blue-400" />
                    <span className="text-white font-semibold text-sm">WealthScope only</span>
                    <span className="ml-auto text-blue-400 text-sm font-bold">$9.99/mo</span>
                  </div>
                  <p className="text-xs text-slate-500">For investors who want to track their financial readiness before buying their first or next property.</p>
                </Link>
                <Link to="/portfolioiq" className="block bg-white/[0.02] border border-white/10 rounded-2xl p-5 hover:border-blue-500/30 transition-colors text-left">
                  <div className="flex items-center gap-2 mb-2">
                    <Building2 className="w-4 h-4 text-blue-400" />
                    <span className="text-white font-semibold text-sm">PortfolioIQ only</span>
                    <span className="ml-auto text-blue-400 text-sm font-bold">$29.99/mo</span>
                  </div>
                  <p className="text-xs text-slate-500">For investors with existing properties who need analytics, DSCR reports, and Schedule E summaries.</p>
                </Link>
              </div>
            </div>
          </section>
        </RevealOnScroll>

        {/* ── Trust ────────────────────────────────────────────── */}
        <RevealOnScroll direction="up">
          <section className="py-12 px-4 border-t border-white/5">
            <div className="max-w-[900px] mx-auto">
              <p className="text-center text-xs font-bold uppercase tracking-widest text-slate-400 mb-8">Built with bank-level security</p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 text-center">
                {[
                  { icon: Shield, label: "AES-256 encryption" },
                  { icon: Shield, label: "Plaid-secured bank sync" },
                  { icon: Check, label: "Row-level data isolation" },
                  { icon: Shield, label: "Google Cloud Armor WAF" },
                ].map(({ icon: Icon, label }) => (
                  <div key={label} className="flex flex-col items-center gap-2">
                    <Icon className="w-4 h-4 text-slate-400" />
                    <p className="text-xs text-slate-400">{label}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>
        </RevealOnScroll>

        {/* ── Bottom CTA ───────────────────────────────────────── */}
        <RevealOnScroll direction="up">
          <section className="py-20 px-4 text-center">
            <h2 className="text-3xl sm:text-4xl font-extrabold text-white mb-4">
              Start with everything. Pay less than buying separately.
            </h2>
            <p className="text-slate-500 mb-2 max-w-md mx-auto">
              14-day free trial. Access WealthScope and PortfolioIQ from day one.
            </p>
            <p className="text-sm text-blue-400 font-semibold mb-8">We'll email you before your trial ends. Cancel anytime.</p>
            <button
              type="button"
              onClick={() => window.open(`${APP_URL}/signup`, "_self")}
              className="inline-flex items-center gap-2 px-10 h-12 rounded-full bg-blue-600 hover:bg-blue-500 text-white font-semibold text-sm transition-colors"
            >
              Get the Bundle — Free for 14 Days <ArrowRight className="w-4 h-4" />
            </button>
          </section>
        </RevealOnScroll>
      </div>
    </>
  );
};

export default BundlePage;
